// What the website really shows, and the "Push Ke Website" pipeline that waits for it.
//
// The storefront reads two caches, both rebuilt by the backend:
// - products-cache (studiosclo.uk/products-cache): products + collections + orders,
//   rebuilt by productsGet?update-get-products-cache=true;
// - size cache (POST /availability/product-size): stock per product, rebuilt right after
//   the products-cache (takes ~1–2 min). Until it covers a product, the website treats it
//   as sold out — so a push isn't "done" until the size cache has caught up.
// publishToWebsite runs the push step by step and reports each step; the panel then checks
// the pushed collections against the fresh live data (collectionVisibility.ts).
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { fetchProductsList } from './ProductListRedux';

const PUBLIC_PRODUCTS_CACHE_URL = 'https://studiosclo.uk/products-cache';
const SIZE_AVAILABILITY_URL = 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/availability/product-size';
const COLLECTION_PUT_URL = 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/collection';
const PRODUCTS_CACHE_REBUILD_URL =
  'https://ck8k2pv7c9.execute-api.ap-southeast-1.amazonaws.com/ProductAPIProduction/products?update-get-products-cache=true';

export const SIZE_CACHE_POLL_MS = 10 * 1000;
export const SIZE_CACHE_MAX_WAIT_MS = 5 * 60 * 1000;

export interface WebsiteSnapshot {
  productsById: { [productId: string]: any };
  collections: any[];
  generalOrder: string[];
  orderDict: { [category: string]: string[] };
  productsCacheTimestamp?: string;
  sizeAvailability: { [productId: string]: any };
  sizeCreationDateTime?: string;
  sizeSourceTimestamp?: string;
  fetchedAt: string;
}

export type PublishStepKey = 'collections' | 'productsCache' | 'sizeCache' | 'verify';
export type PublishStepState = 'pending' | 'running' | 'done' | 'error' | 'skipped';

export interface PublishStep {
  key: PublishStepKey;
  label: string;
  state: PublishStepState;
  detail?: string;
}

export interface DroppedProduct {
  productId: string;
  reasons: string[];
}

interface WebsiteStatusState {
  snapshot: WebsiteSnapshot | null;
  snapshotStatus: APIRequestStatus;
  snapshotError: string | null;
  publish: {
    open: boolean;
    running: boolean;
    steps: PublishStep[];
    dropped: DroppedProduct[];
    // Collections to check once the push is live.
    checkCollectionIds: string[];
    finished: boolean;
  };
}

const initialSteps = (withCollections: boolean): PublishStep[] => [
  { key: 'collections', label: 'Simpan koleksi', state: withCollections ? 'pending' : 'skipped' },
  { key: 'productsCache', label: 'Perbarui data produk website', state: 'pending' },
  { key: 'sizeCache', label: 'Perbarui data stok/ukuran website (±1–2 menit)', state: 'pending' },
  { key: 'verify', label: 'Cek tampilan di website', state: 'pending' }
];

const initialState: WebsiteStatusState = {
  snapshot: null,
  snapshotStatus: APIRequestStatus.Idle,
  snapshotError: null,
  publish: {
    open: false,
    running: false,
    steps: initialSteps(false),
    dropped: [],
    checkCollectionIds: [],
    finished: false
  }
};

function errorText(err: any): string {
  return err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? String(err);
}

async function fetchSizeCache(): Promise<{ sizeAvailability: any; creationDateTime?: string; sourceProductsTimestamp?: string }> {
  const response = await axios({
    method: 'post',
    url: SIZE_AVAILABILITY_URL,
    data: JSON.stringify({ productIdArray: [], measurementsArray: [] })
  });
  const data = response.data ?? {};
  return {
    sizeAvailability: data.sizeAvailability ? JSON.parse(data.sizeAvailability) : {},
    creationDateTime: data.creationDateTime,
    sourceProductsTimestamp: data.sourceProductsTimestamp
  };
}

export const fetchWebsiteSnapshot = createAsyncThunk('websiteStatus/fetchWebsiteSnapshot', async (): Promise<WebsiteSnapshot> => {
  const [productsCache, sizeCache] = await Promise.all([
    // The query only defeats browser caching; the CDN serves the same object.
    axios({ method: 'get', url: `${PUBLIC_PRODUCTS_CACHE_URL}?v=${Date.now()}` }).then((r) => r.data),
    fetchSizeCache()
  ]);
  const productsById: { [productId: string]: any } = {};
  for (const product of productsCache?.products ?? []) productsById[product.productId] = product;
  return {
    productsById,
    collections: productsCache?.collections ?? [],
    generalOrder: productsCache?.discoveryCollectionsOrder ?? [],
    orderDict: productsCache?.discoveryCollectionsOrderDict ?? {},
    productsCacheTimestamp: productsCache?.productUpdateTimestamp,
    sizeAvailability: sizeCache.sizeAvailability,
    sizeCreationDateTime: sizeCache.creationDateTime,
    sizeSourceTimestamp: sizeCache.sourceProductsTimestamp,
    fetchedAt: new Date().toISOString()
  };
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// True once the size cache was built from (at least) the products-cache we just pushed.
export function sizeCacheCaughtUp(
  sizeCache: { creationDateTime?: string; sourceProductsTimestamp?: string },
  productUpdateTimestamp: string | undefined,
  pushStartedAt: string
): boolean {
  if (productUpdateTimestamp && sizeCache.sourceProductsTimestamp) {
    return sizeCache.sourceProductsTimestamp >= productUpdateTimestamp;
  }
  // Older backend without source stamps: any rebuild after the push started.
  return !!sizeCache.creationDateTime && sizeCache.creationDateTime >= pushStartedAt;
}

export const publishToWebsite = createAsyncThunk(
  'websiteStatus/publishToWebsite',
  async (
    arg: { collectionsPayload?: any; checkCollectionIds: string[] },
    thunkAPI
  ) => {
    const step = (key: PublishStepKey, state: PublishStepState, detail?: string) =>
      thunkAPI.dispatch(setPublishStep({ key, state, detail }));
    const pushStartedAt = new Date().toISOString();

    // 1. Collections (collection push only). A failed write must stop everything —
    // the drafts stay so nothing is lost.
    if (arg.collectionsPayload) {
      step('collections', 'running');
      try {
        const response = await axios({ method: 'put', url: COLLECTION_PUT_URL, data: JSON.stringify(arg.collectionsPayload) });
        if (response.data?.success === false
          || response.data?.updateCollectionTableSuccess === false
          || response.data?.updateDiscoveryCollectionsOrder === false
          || response.data?.updateDiscoveryCollectionsOrderDict === false) {
          throw new Error('sebagian data koleksi gagal disimpan');
        }
        step('collections', 'done', `${arg.collectionsPayload.collectionsListDraft?.length ?? 0} koleksi + urutan tersimpan`);
      } catch (err) {
        const failed = (err as any)?.response?.data?.failedCollectionIds;
        step('collections', 'error', `Gagal: ${errorText(err)}${failed?.length ? ` (${failed.join(', ')})` : ''} — perubahan belum hilang, coba push lagi`);
        throw err;
      }
      // Drafts now match the server; reload the admin copy.
      thunkAPI.dispatch(fetchProductsList());
    }

    // 2. Products-cache (what the website reads), with the backend's drop report.
    step('productsCache', 'running');
    let productUpdateTimestamp: string | undefined;
    try {
      const response = await axios({ method: 'get', url: PRODUCTS_CACHE_REBUILD_URL });
      const rebuild = response.data?.cacheRebuild;
      productUpdateTimestamp = rebuild?.productUpdateTimestamp;
      const dropped: DroppedProduct[] = rebuild?.dropped ?? [];
      thunkAPI.dispatch(setPublishDropped(dropped));
      step('productsCache', 'done',
        `${response.data?.productIdList?.length ?? '?'} produk di website` + (dropped.length ? `, ${dropped.length} tidak masuk (lihat di bawah)` : ''));
    } catch (err) {
      step('productsCache', 'error', `Gagal: ${errorText(err)} — coba push lagi`);
      throw err;
    }

    // 3. Wait for the size cache to cover the new products-cache.
    step('sizeCache', 'running');
    const deadline = Date.now() + SIZE_CACHE_MAX_WAIT_MS;
    let caughtUp = false;
    while (Date.now() < deadline) {
      try {
        const sizeCache = await fetchSizeCache();
        if (sizeCacheCaughtUp(sizeCache, productUpdateTimestamp, pushStartedAt)) {
          caughtUp = true;
          break;
        }
      } catch (err) {
        // Transient — keep polling until the deadline.
      }
      await sleep(SIZE_CACHE_POLL_MS);
    }
    if (caughtUp) {
      step('sizeCache', 'done');
    } else {
      step('sizeCache', 'error',
        'Belum selesai setelah 5 menit — produk baru mungkin masih tampil habis. Cek lagi nanti (tombol "Cek ulang")');
    }

    // 4. Fresh live data → the panel diagnoses the pushed collections.
    step('verify', 'running');
    await thunkAPI.dispatch(fetchWebsiteSnapshot());
    step('verify', 'done');
    return true;
  }
);

export const slice = createSlice({
  name: 'websiteStatus',
  initialState,
  reducers: {
    setPublishStep(state, action: PayloadAction<{ key: PublishStepKey; state: PublishStepState; detail?: string }>) {
      const s = state.publish.steps.find((x) => x.key === action.payload.key);
      if (s) {
        s.state = action.payload.state;
        s.detail = action.payload.detail;
      }
    },
    setPublishDropped(state, action: PayloadAction<DroppedProduct[]>) {
      state.publish.dropped = action.payload;
    },
    closePublishPanel(state) {
      state.publish.open = false;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchWebsiteSnapshot.pending, (state) => {
        state.snapshotStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchWebsiteSnapshot.fulfilled, (state, action) => {
        state.snapshot = action.payload;
        state.snapshotStatus = APIRequestStatus.Success;
        state.snapshotError = null;
      })
      .addCase(fetchWebsiteSnapshot.rejected, (state, action) => {
        state.snapshotStatus = APIRequestStatus.Failure;
        state.snapshotError = action.error.message ?? 'gagal';
      })
      .addCase(publishToWebsite.pending, (state, action) => {
        state.publish = {
          open: true,
          running: true,
          steps: initialSteps(!!action.meta.arg.collectionsPayload),
          dropped: [],
          checkCollectionIds: action.meta.arg.checkCollectionIds,
          finished: false
        };
      })
      .addCase(publishToWebsite.fulfilled, (state) => {
        state.publish.running = false;
        state.publish.finished = true;
      })
      .addCase(publishToWebsite.rejected, (state) => {
        state.publish.running = false;
        state.publish.finished = true;
      });
  }
});

export const { setPublishStep, setPublishDropped, closePublishPanel } = slice.actions;

export default slice.reducer;
